"use client";

import _slicedToArray from "/codebuild/output/src934747875/src/actions-runner/_work/www-lwb-org-au/www-lwb-org-au/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { useFocusable } from "./LVA2YJMS.js";
import { createElement, createHook, forwardRef } from "./LMDWO4NN.js";
import { useEvent, useMergeRefs, useMetadataProps } from "./ABQUS43J.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/command/command.tsx
import { isButton, isTextField } from "@ariakit/core/utils/dom";
import { fireClickEvent, isSelfTarget, queueBeforeEvent } from "@ariakit/core/utils/events";
import { disabledFromProps } from "@ariakit/core/utils/misc";
import { isFirefox } from "@ariakit/core/utils/platform";
import { useEffect, useRef, useState } from "react";
var TagName = "button";
function isNativeClick(event) {
  if (!event.isTrusted) return false;
  var element = event.currentTarget;
  if (event.key === "Enter") {
    return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "A";
  }
  if (event.key === " ") {
    return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "INPUT" || element.tagName === "SELECT";
  }
  return false;
}
var symbol = Symbol("command");
var useCommand = createHook(function useCommand2(_a) {
  var _b = _a,
    _b$clickOnEnter = _b.clickOnEnter,
    clickOnEnter = _b$clickOnEnter === void 0 ? true : _b$clickOnEnter,
    _b$clickOnSpace = _b.clickOnSpace,
    clickOnSpace = _b$clickOnSpace === void 0 ? true : _b$clickOnSpace,
    props = __objRest(_b, ["clickOnEnter", "clickOnSpace"]);
  var ref = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isNativeButton = _useState2[0],
    setIsNativeButton = _useState2[1];
  useEffect(function () {
    if (!ref.current) return;
    setIsNativeButton(isButton(ref.current));
  }, []);
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    active = _useState4[0],
    setActive = _useState4[1];
  var activeRef = useRef(false);
  var disabled = disabledFromProps(props);
  var _useMetadataProps = useMetadataProps(props, symbol, true),
    _useMetadataProps2 = _slicedToArray(_useMetadataProps, 2),
    isDuplicate = _useMetadataProps2[0],
    metadataProps = _useMetadataProps2[1];
  var onKeyDownProp = props.onKeyDown;
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    var element = event.currentTarget;
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (disabled) return;
    if (!isSelfTarget(event)) return;
    if (isTextField(element)) return;
    if (element.isContentEditable) return;
    var isEnter = clickOnEnter && event.key === "Enter";
    var isSpace = clickOnSpace && event.key === " ";
    var shouldPreventEnter = event.key === "Enter" && !clickOnEnter;
    var shouldPreventSpace = event.key === " " && !clickOnSpace;
    if (shouldPreventEnter || shouldPreventSpace) {
      event.preventDefault();
      return;
    }
    if (isEnter || isSpace) {
      var nativeClick = isNativeClick(event);
      if (isEnter) {
        if (!nativeClick) {
          event.preventDefault();
          var _a2 = event,
            view = _a2.view,
            eventInit = __objRest(_a2, ["view"]);
          var click = function click() {
            return fireClickEvent(element, eventInit);
          };
          if (isFirefox()) {
            queueBeforeEvent(element, "keyup", click);
          } else {
            queueMicrotask(click);
          }
        }
      } else if (isSpace) {
        activeRef.current = true;
        if (!nativeClick) {
          event.preventDefault();
          setActive(true);
        }
      }
    }
  });
  var onKeyUpProp = props.onKeyUp;
  var onKeyUp = useEvent(function (event) {
    onKeyUpProp == null ? void 0 : onKeyUpProp(event);
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (disabled) return;
    if (event.metaKey) return;
    var isSpace = clickOnSpace && event.key === " ";
    if (activeRef.current && isSpace) {
      activeRef.current = false;
      if (!isNativeClick(event)) {
        event.preventDefault();
        setActive(false);
        var element = event.currentTarget;
        var _a2 = event,
          view = _a2.view,
          eventInit = __objRest(_a2, ["view"]);
        queueMicrotask(function () {
          return fireClickEvent(element, eventInit);
        });
      }
    }
  });
  props = __spreadProps(__spreadValues(__spreadValues({
    "data-active": active || void 0,
    type: isNativeButton ? "button" : void 0
  }, metadataProps), props), {
    ref: useMergeRefs(ref, props.ref),
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp
  });
  props = useFocusable(props);
  return props;
});
var Command = forwardRef(function Command2(props) {
  var htmlProps = useCommand(props);
  return createElement(TagName, htmlProps);
});
export { useCommand, Command };